import { useEffect, useState } from 'react';
import { Pin, Plus, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth, hasRole } from '../lib/auth';
import { useI18n } from '../lib/i18n';

/**
 * "Add to view": dropdown en la tarjeta del claim (debajo de los botones de decisión) para
 * COPIAR el claim a una cola del menú izquierdo (ej. Ready to be filed). Agrupado por depto.
 * Las vistas se resuelven en el backend a su primer estatus real (Waiting to Schedule →
 * Claim in) para que el claim aparezca de verdad en esa cola. office+ (pedido del dueño
 * 2026-07-22: que le salga a TODOS los usuarios).
 */
export function AddToViewSelect({ claimId, onChanged }: { claimId: string; onChanged?: () => void }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const canCopy = hasRole(me, 'office');
  const [queues, setQueues] = useState<Array<{ deptKey: string; deptName: string; statuses: Array<{ key: string; label: string }> }>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!canCopy) return;
    let alive = true;
    api.get<{ departments: Array<{ key: string; name: string }> }>('/api/departments')
      .then(async (r) => {
        const out: Array<{ deptKey: string; deptName: string; statuses: Array<{ key: string; label: string }> }> = [];
        for (const d of r.departments) {
          try {
            // COC también sale en la lista: su cola es propia (claim_coc), así que al elegirla
            // se llama al enqueue de COC en vez del status de depto (ver copyToQueue).
            const s = (await api.get<{ statuses: Array<{ key: string; label: string }> }>(`/api/departments/${d.key}/statuses`)).statuses;
            if (s.length) out.push({ deptKey: d.key, deptName: d.name, statuses: s });
          } catch { /* depto sin estatus configurables */ }
        }
        if (alive) setQueues(out);
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCopy]);

  // Cola elegida (aún sin ejecutar) + MOTIVO: elegir del dropdown ya no dispara al instante —
  // aparece el cuadro "¿por qué va ahí?" y el botón Copy. El motivo se publica como UPDATE en el
  // canal del depto destino, firmado por quien lo hizo (el endpoint de chat pone el autor).
  const [picked, setPicked] = useState('');
  const [note, setNote] = useState('');

  async function copyToQueue() {
    const [deptKey, statusKey] = picked.split('::');
    if (!deptKey || !statusKey) return;
    const q = queues.find((x) => x.deptKey === deptKey);
    const label = q?.statuses.find((s) => s.key === statusKey)?.label ?? statusKey;
    setBusy(true);
    try {
      // COC no es un status de depto: es su cola propia (claim_coc) → endpoint de enqueue
      // (ya es idempotente: si el claim está en la cola, no duplica).
      if (deptKey === 'claims' && statusKey === 'COC') {
        await api.post(`/api/claims/${claimId}/coc/enqueue`, {});
      } else {
        // failIfPresent: si el claim YA está en esa vista, el server responde 409 y NO lo
        // vuelve a meter (pedido de Manuel: repetir la vista no está permitido).
        await api.post(`/api/claims/${claimId}/departments/${deptKey}/status`, { status: statusKey, failIfPresent: true });
      }
      // El motivo → update del depto destino (best-effort: la copia ya quedó aunque falle la nota).
      if (note.trim()) {
        try {
          await api.post(`/api/claims/${claimId}/chat`, {
            department: deptKey,
            body: `${t('copyq.notePrefix')} "${q?.deptName ?? deptKey} → ${label}": ${note.trim()}`,
          });
        } catch { /* la nota es secundaria */ }
      }
      alert(`${t('copyq.done')} "${q?.deptName ?? deptKey} → ${label}".`);
      setPicked(''); setNote('');
      onChanged?.();
    } catch (e) {
      // 409 = ya está en esa vista: se avisa con claridad en vez de "no se pudo copiar".
      if (e instanceof ApiError && e.message.includes('already_in_view')) {
        alert(t('copyq.alreadyIn', { view: `${q?.deptName ?? deptKey} → ${label}` }));
        setPicked(''); setNote('');
      } else alert(t('copyq.fail'));
    } finally { setBusy(false); }
  }

  if (!canCopy || queues.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
      <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><Plus size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('copyq.addToView')}</span>
      <select
        className="input"
        style={{ maxWidth: 240 }}
        disabled={busy}
        value={picked}
        onChange={(e) => setPicked(e.target.value)}
      >
        <option value="">—</option>
        {queues.map((q) => (
          <optgroup key={q.deptKey} label={q.deptName}>
            {q.statuses.map((s) => <option key={s.key} value={`${q.deptKey}::${s.key}`}>{s.label}</option>)}
          </optgroup>
        ))}
      </select>
      {picked && (
        <>
          <input
            className="input sm"
            style={{ flex: 1, minWidth: 200, maxWidth: 380 }}
            placeholder={t('copyq.whyPh')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') copyToQueue(); }}
          />
          <button className="btn sm" disabled={busy} onClick={copyToQueue}>{busy ? '…' : <><Pin size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('copyq.copyBtn')}</>}</button>
          <button className="btn ghost sm" disabled={busy} onClick={() => { setPicked(''); setNote(''); }}><X size={14} strokeWidth={1.75} /></button>
        </>
      )}
    </div>
  );
}
